import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TableSkeletonLoaderComponent } from '@shared/ui/table-skeleton-loader/table-skeleton-loader.component';
import { SlideInRightModalComponent } from '@shared/ui/slide-in-right-modal/slide-in-right-modal.component';
import { FiltersComponent } from '@shared/ui/filters/filters.component';
import { TableNavigatorComponent } from '@shared/ui/table-navigator/table-navigator.component';
import {
  ReferralsService,
  referredNames,
  ReferredUser,
} from '@core/services/referrals.service';
import { Pagination } from '@core/models/pagination.model';
import { Subscription } from 'rxjs';
import { IFILTER } from 'app/app.constants';

@Component({
  selector: 'app-referred-accounts-list',
  standalone: true,
  imports: [
    CommonModule,
    TableSkeletonLoaderComponent,
    SlideInRightModalComponent,
    FiltersComponent,
    TableNavigatorComponent,
  ],
  templateUrl: './referred-accounts-list.component.html',
})
export class ReferredAccountsListComponent implements OnInit, OnDestroy {
  referralCode: string = '';
  userId: string = '';
  isLoading: boolean = true;
  totalReferrals: number = 0;
  referredUsers: referredNames[] = [];

  // Pagination properties
  currentPage: number = 1;
  pageSize: number = 10;
  currentSkip: number = 0;

  // Filter properties
  isFilterActive = false;
  hadFilterBefore = false;

  private referralsService = inject(ReferralsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private change = inject(ChangeDetectorRef);
  private subs: Subscription[] = [];

  filterStructure: IFILTER[] = [
    {
      filterKey: 'start_date',
      label: 'Start Date',
      type: 'date',
    },
    {
      filterKey: 'end_date',
      label: 'End Date',
      type: 'date',
    }
  ];

  constructor() {}

  ngOnInit() {
    this.isLoading = true;
    this.extractRouteParams();
    this.filterEventListener();
  }

  private extractRouteParams() {
    // Extract BOTH user_id and referralCode from query params
    const queryParamsSub = this.route.queryParams.subscribe((queryParams) => {
      this.referralCode = queryParams['referralCode'] || '';
      this.userId = queryParams['user_id'] || '';
    });

    this.subs.push(queryParamsSub);
  }

  filterEventListener() {
    this.route.queryParams.subscribe((params) => {
      // Skip if we don't have required params yet
      if (!this.userId || !this.referralCode) return;

      let query = "";
      
      // Build search query
      if (params['search']) {
        query += `search_text=${params['search']}&`;
      }

      // Build date filters
      if (params['start_date']) {
        query += `start_date=${params['start_date']}&`;
      }

      if (params['end_date']) {
        query += `end_date=${params['end_date']}&`;
      }

      // Handling pagination
      const rawPage = params['page'] ? parseInt(params['page']) : 1;
      this.currentPage = rawPage;
      const page = rawPage > 1 ? rawPage - 1 : 0;
      const take = this.pageSize;
      const skip = page * take;
      this.currentSkip = skip;

      
      query += `skip=${skip}&take=${take}`;

      
      const hasActiveFilters = params['search'] || params['start_date'] || params['end_date'];
      
      if (hasActiveFilters) {
        this.isFilterActive = true;
        this.hadFilterBefore = true;
        this.loadReferredUsersWithFilter(query);
      } else {
       
        if (this.hadFilterBefore || params['page']) {
          this.hadFilterBefore = false;
        }
        this.loadReferredUsers(skip, take);
      }
    });
  }

  loadReferredUsers(skip: number = 0, take: number = 10) {
    this.isLoading = true;
    
    const sub = this.referralsService
      .getReferredUsersByReferralCode(this.userId, this.referralCode, skip, take)
      .subscribe({
        next: (res) => {
          this.referredUsers = res?.data?.referred_user_names;
          this.totalReferrals = res?.data?.total_count || 0;
          this.isLoading = false;
          this.change.detectChanges();
        },
        error: (error) => {
          console.error('Error loading referred users:', error);
          this.referredUsers = [];
          this.totalReferrals = 0;
          this.isLoading = false;
          this.change.detectChanges();
        },
      });

    this.subs.push(sub);
  }

  loadReferredUsersWithFilter(filterQuery: string) {
    this.isLoading = true;
    
    const sub = this.referralsService
      .getReferredUsersByReferralCodeWithFilter(this.userId, this.referralCode, filterQuery)
      .subscribe({
        next: (res) => {
          this.referredUsers = res?.data?.referred_user_names || [];
          this.totalReferrals = res?.data?.total_count || 0;
          this.isLoading = false;
          this.change.detectChanges();
        },
        error: (error) => {
          console.error('Error loading filtered referred users:', error);
          this.referredUsers = [];
          this.totalReferrals = 0;
          this.isLoading = false;
          this.change.detectChanges();
        },
      });

    this.subs.push(sub);
  }

  navigate(currentPage: number) {
    this.router.navigate([], {
      queryParams: { page: currentPage },
      queryParamsHandling: 'merge',
    });
  }

  getRowIndex(index: number): number {
    return this.currentSkip + index + 1;
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}